import { Helmet } from 'react-helmet-async';

// Public-safe realtor data (from public_realtor_profiles view)
interface RealtorData {
  first_name: string | null;
  last_name: string | null;
  photo_url: string | null;
  phone: string | null;
  city: string | null;
  state: string | null;
  license_numbers: string | null;
  about_me: string | null;
}

interface LocationData {
  name: string;
  state: string | null;
  zip_codes?: string[] | null;
}

interface StructuredDataProps {
  realtor: RealtorData | null;
  siteName: string;
  siteUrl?: string;
  town?: LocationData;
  area?: LocationData & { townName: string };
  pageUrl: string;
}

export function StructuredData({ 
  realtor, 
  siteName, 
  siteUrl = typeof window !== 'undefined' ? window.location.origin : '',
  town,
  area,
  pageUrl
}: StructuredDataProps) {
  const fullName = realtor ? [realtor.first_name, realtor.last_name].filter(Boolean).join(' ') : '';
  // Use city/state for location (not full address for privacy)
  const locationDisplay = realtor ? [realtor.city, realtor.state].filter(Boolean).join(', ') : '';
  
  const locationName = area ? `${area.name}, ${area.townName}` : town?.name || '';
  const stateName = area?.state || town?.state || 'MA';
  const stateFullName = stateName === 'MA' ? 'Massachusetts' : stateName;

  // Service areas from zip codes
  const serviceAreas = (area?.zip_codes || town?.zip_codes || []).map(zip => ({
    "@type": "PostalAddress",
    "postalCode": zip,
    "addressRegion": stateName,
    "addressCountry": "US"
  }));

  // Person Schema for the Realtor (privacy-safe - no street address)
  const personSchema = realtor && fullName ? {
    "@context": "https://schema.org",
    "@type": "Person",
    "@id": `${siteUrl}/#realtor`,
    "name": fullName,
    "jobTitle": "Real Estate Agent",
    "telephone": realtor.phone || undefined,
    "image": realtor.photo_url || undefined,
    "description": realtor.about_me || `Real estate agent serving ${locationName}, ${stateFullName}`,
    "address": realtor.city ? {
      "@type": "PostalAddress",
      "addressLocality": realtor.city || undefined,
      "addressRegion": realtor.state || undefined,
      "addressCountry": "US"
    } : undefined,
    "worksFor": {
      "@type": "RealEstateAgent",
      "@id": `${siteUrl}/#agency`
    }
  } : null;

  // RealEstateAgent Schema (LocalBusiness) - privacy-safe
  const realEstateAgentSchema = {
    "@context": "https://schema.org",
    "@type": "RealEstateAgent",
    "@id": `${siteUrl}/#agency`,
    "name": fullName || siteName,
    "description": `Professional real estate services in ${locationName}, ${stateFullName}. Helping buyers and sellers navigate the local real estate market.`,
    "url": siteUrl,
    "telephone": realtor?.phone || undefined,
    "image": realtor?.photo_url || undefined,
    "address": realtor?.city ? {
      "@type": "PostalAddress",
      "addressLocality": realtor.city || undefined,
      "addressRegion": realtor.state || undefined,
      "addressCountry": "US"
    } : undefined,
    "areaServed": serviceAreas.length > 0 ? serviceAreas : {
      "@type": "Place",
      "name": `${locationName}, ${stateFullName}`
    },
    "priceRange": "$$-$$$$",
    "openingHoursSpecification": {
      "@type": "OpeningHoursSpecification",
      "dayOfWeek": ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"],
      "opens": "09:00",
      "closes": "18:00"
    }
  };

  // Service Schema
  const serviceSchema = {
    "@context": "https://schema.org",
    "@type": "Service",
    "serviceType": "Real Estate Services",
    "provider": {
      "@type": "RealEstateAgent",
      "@id": `${siteUrl}/#agency`
    },
    "areaServed": [
      {
        "@type": "City",
        "name": area ? area.townName : (town?.name || ''),
        "containedInPlace": {
          "@type": "State",
          "name": stateFullName
        }
      },
      ...(area ? [{
        "@type": "Neighborhood",
        "name": area.name,
        "containedInPlace": {
          "@type": "City",
          "name": area.townName
        }
      }] : [])
    ],
    "description": `Professional real estate services including buying, selling, and property consultation in ${locationName}, ${stateFullName}.`,
    "offers": {
      "@type": "Offer",
      "availability": "https://schema.org/InStock"
    }
  };

  // Extract domain name for alternateName (e.g., "crippinrealestate.com" from URL)
  const domainName = siteUrl ? siteUrl.replace(/^https?:\/\//, '').replace(/\/$/, '') : '';

  // WebSite Schema - standalone for Google site name recognition
  const webSiteSchema = {
    "@context": "https://schema.org",
    "@type": "WebSite",
    "@id": `${siteUrl}/#website`,
    "name": siteName,
    "alternateName": domainName || undefined,
    "url": siteUrl,
    "description": `Professional real estate services in ${locationName}, ${stateFullName}.`,
    "publisher": {
      "@type": "RealEstateAgent",
      "@id": `${siteUrl}/#agency`
    }
  };

  // WebPage Schema
  const webPageSchema = {
    "@context": "https://schema.org",
    "@type": "WebPage",
    "url": pageUrl,
    "name": `${locationName} Real Estate | ${fullName || siteName}`,
    "description": `Find homes for sale in ${locationName}, ${stateFullName}. ${fullName || 'Your local real estate expert'} provides professional real estate services.`,
    "isPartOf": {
      "@type": "WebSite",
      "@id": `${siteUrl}/#website`
    },
    "about": {
      "@type": "Place",
      "name": locationName,
      "address": {
        "@type": "PostalAddress",
        "addressLocality": area ? area.townName : (town?.name || ''),
        "addressRegion": stateFullName,
        "addressCountry": "US"
      }
    },
    "mainEntity": {
      "@type": "RealEstateAgent",
      "@id": `${siteUrl}/#agency`
    }
  };

  // Combine all schemas
  const schemas = [
    webSiteSchema,
    realEstateAgentSchema,
    serviceSchema,
    webPageSchema,
    ...(personSchema ? [personSchema] : [])
  ];

  return (
    <Helmet>
      {schemas.map((schema, index) => (
        <script key={index} type="application/ld+json">
          {JSON.stringify(schema, null, 0)}
        </script>
      ))}
    </Helmet>
  );
}
